import { CheckCircle, Edit, FileText, LogOut, Minus, Package, Plus, Send, ShoppingCart, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Input } from '../components/common/Input';
import Footer from '../components/layout/Footer';
import { useMenu } from '../context/MenuContext';
import ordersService from '../services/ordersService';
import { media, theme } from '../styles/theme';

const Container = styled.div`
  min-height: 100vh;
  background-color: ${theme.colors.background};
  padding: ${theme.spacing.lg};
  padding-bottom: 80px;
  position: relative;

  ${media.down.sm} {
    padding: ${theme.spacing.sm};
    padding-bottom: 100px;
  }

  ${media.down.xs} {
    padding: ${theme.spacing.xs};
    padding-bottom: 100px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xl};
  padding: ${theme.spacing.lg};
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};
  flex-wrap: wrap;
  gap: ${theme.spacing.md};

  h1 {
    font-size: ${theme.typography.fontSize['2xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.text};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.md};
    h1 {
      font-size: ${theme.typography.fontSize.xl};
    }
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.lg};
  flex-wrap: wrap;

  ${media.down.sm} {
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing.md};
    width: 100%;
  }
`;

const TableInfo = styled.span`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text};
  font-weight: ${theme.typography.fontWeight.medium};

  ${media.down.sm} {
    font-size: ${theme.typography.fontSize.sm};
  }
`;

const LogoutButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  ${media.down.sm} {
    width: 100%;
    justify-content: center;

    .logout-text {
      display: inline;
    }
  }
`;

const TableGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.xl};

  ${media.down.md} {
    grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
    gap: ${theme.spacing.sm};
  }

  ${media.down.sm} {
    grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
    gap: ${theme.spacing.sm};
  }

  ${media.down.xs} {
    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
    gap: ${theme.spacing.xs};
  }
`;

const TableCard = styled(Card)`
  padding: ${theme.spacing.lg};
  text-align: center;
  cursor: pointer;
  border: 3px solid ${({ selected }) => selected ? theme.colors.primary : 'transparent'};
  background-color: ${({ status, selected }) => {
    if (selected) return `${theme.colors.primary}10`;
    if (status === 'pending') return `${theme.colors.status.pending}20`;
    if (status === 'preparing') return `${theme.colors.status.preparing}20`;
    if (status === 'ready_for_billing') return `${theme.colors.status.ready}20`;
    return theme.colors.white;
  }};
  transition: all 0.2s ease;
  min-height: 80px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
  }

  &:active {
    transform: scale(0.98);
  }

  h3 {
    font-size: ${theme.typography.fontSize.lg};
    font-weight: ${theme.typography.fontWeight.semiBold};
    margin-bottom: ${theme.spacing.xs};
    color: ${theme.colors.text};
  }

  p {
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.textSecondary};
  }

  ${media.down.sm} {
    padding: ${theme.spacing.md};
    min-height: 70px;
    
    h3 {
      font-size: ${theme.typography.fontSize.base};
    }
    
    p {
      font-size: ${theme.typography.fontSize.xs};
    }
  }

  ${media.down.xs} {
    padding: ${theme.spacing.sm};
    min-height: 60px;
    
    h3 {
      font-size: ${theme.typography.fontSize.sm};
    }
    
    p {
      font-size: 10px;
    }
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: ${theme.spacing.lg};

  ${media.down.lg} {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
  }
`;

const MenuSection = styled.div`
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.md};
  max-height: calc(100vh - 200px);
  overflow-y: auto;

  ${media.down.lg} {
    max-height: none;
    margin-bottom: ${theme.spacing.lg};
  }

  ${media.down.sm} {
    padding: ${theme.spacing.md};
    /* leave extra space at bottom so fixed mobile cart doesn't overlap content */
    padding-bottom: ${theme.spacing['4xl']};
  }
`;

const CategoryTabs = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
  flex-wrap: wrap;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-bottom: ${theme.spacing.xs};

  &::-webkit-scrollbar {
    display: none;
  }

  ${media.down.sm} {
    gap: ${theme.spacing.xs};
    margin-bottom: ${theme.spacing.md};
  }
`;

const CategoryTab = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.full};
  border: 2px solid ${({ active }) => active ? theme.colors.primary : theme.colors.border};
  background-color: ${({ active }) => active ? theme.colors.primary : theme.colors.white};
  color: ${({ active }) => active ? theme.colors.white : theme.colors.text};
  font-weight: ${({ active }) => active ? theme.typography.fontWeight.semiBold : theme.typography.fontWeight.regular};
  font-family: ${theme.typography.fontFamily};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${theme.typography.fontSize.sm};
  white-space: nowrap;
  min-height: 44px;
  -webkit-tap-highlight-color: transparent;

  &:active {
    transform: scale(0.95);
  }

  ${media.down.sm} {
    padding: ${theme.spacing.xs} ${theme.spacing.md};
    font-size: ${theme.typography.fontSize.xs};
    min-height: 40px;
  }
`;

const MenuGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${theme.spacing.lg};

  ${media.down.md} {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: ${theme.spacing.md};
  }

  ${media.down.sm} {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.sm};
  }
`;

const MenuItemCard = styled(Card)`
  padding: ${theme.spacing.sm};
  position: relative;
  opacity: ${({ disabled }) => disabled ? 0.5 : 1};
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  min-height: 70px;
  display: flex;
  flex-direction: column;

  ${({ disabled }) => !disabled && `
    &:hover {
      transform: translateY(-2px);
      box-shadow: ${theme.shadows.md};
    }

    &:active {
      transform: scale(0.98);
    }
  `}

  ${media.down.sm} {
    padding: 8px;
    min-height: 55px;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  ${media.down.xs} {
    padding: 6px;
    min-height: 50px;
  }
`;

const AvailabilityBadge = styled.span`
  position: absolute;
  top: ${theme.spacing.sm};
  right: ${theme.spacing.sm};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semiBold};
  background-color: ${({ available }) => available ? theme.colors.status.ready : theme.colors.status.outOfStock};
  color: ${theme.colors.white};

  ${media.down.sm} {
    position: static;
    padding: 2px 6px;
    font-size: 9px;
    order: 3;
  }
`;

const MenuItemName = styled.h3`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semiBold};
  margin-bottom: ${theme.spacing.xs};
  color: ${theme.colors.text};
  line-height: 1.3;

  ${media.down.sm} {
    font-size: 11px;
    margin-bottom: 2px;
    flex: 1;
    order: 1;
  }
`;

const MenuItemPrice = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.md};

  ${media.down.sm} {
    font-size: 11px;
    margin-bottom: 0;
    order: 2;
    min-width: 40px;
    text-align: right;
  }
`;

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  justify-content: space-between;

  ${media.down.sm} {
    order: 4;

    button {
      width: 28px;
      height: 28px;
      min-width: 28px;
      min-height: 28px;
      padding: 0;

      svg {
        width: 14px;
        height: 14px;
      }
    }
  }
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  border: 2px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.xs};
  flex-shrink: 0;

  ${media.down.sm} {
    gap: 4px;
    padding: 2px;
    border-width: 1px;
  }
`;

const QuantityButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.md};
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.text};
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: ${theme.typography.fontFamily};
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:hover {
    background-color: ${theme.colors.primary};
    color: ${theme.colors.white};
  }

  &:active {
    transform: scale(0.9);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${media.down.sm} {
    width: 36px;
    height: 36px;
    min-width: 36px;
    min-height: 36px;
  }
`;

const QuantityValue = styled.span`
  font-weight: ${theme.typography.fontWeight.semiBold};
  min-width: 30px;
  text-align: center;

  ${media.down.sm} {
    font-size: 11px;
    min-width: 20px;
  }
`;


const CartSection = styled.div`
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.md};
  position: sticky;
  top: ${theme.spacing.lg};
  max-height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  pointer-events: auto;
  z-index: 1;

  ${media.down.lg} {
    position: relative;
    top: 0;
    max-height: none;
    margin-bottom: ${theme.spacing.lg};
  }

  ${media.down.sm} {
    display: none;
    padding: ${theme.spacing.md};
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    max-height: 70vh;
    height: auto;
    border-radius: ${theme.borderRadius.xl} ${theme.borderRadius.xl} 0 0;
    box-shadow: ${theme.shadows['2xl']};
    z-index: ${theme.zIndex.modal};
    transition: transform 0.3s ease;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    pointer-events: auto;
    overscroll-behavior: contain;
    
    &.open {
      display: flex;
      transform: translateY(0);
      pointer-events: auto;
    }
  }

  ${media.down.xs} {
    max-height: 75vh;
    padding: ${theme.spacing.sm};
  }
`;

const CartBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: ${theme.zIndex.modalBackdrop};
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
  pointer-events: none;

  &.open {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }

  ${media.up.sm} {
    display: none;
  }
`;

const CartHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.border};

  h2 {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.semiBold};
    color: ${theme.colors.text};
  }

  ${media.down.sm} {
    margin-bottom: 10px;
    padding-bottom: 8px;

    h2 {
      font-size: 14px;
    }

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const CartItems = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-bottom: ${theme.spacing.lg};

  ${media.down.sm} {
    flex: none;
    overflow-y: visible;
    margin-bottom: 8px;
    max-height: none;
  }
`;

const CartItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.sm};
  background-color: ${theme.colors.secondary};
  border-radius: ${theme.borderRadius.md};
  gap: ${theme.spacing.sm};

  ${media.down.sm} {
    padding: 6px;
    gap: 4px;
    margin-bottom: 4px;
    border-radius: 6px;
  }
`;

const CartItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${theme.spacing.sm};

  ${media.down.sm} {
    flex-direction: row;
    align-items: flex-start;
    gap: 6px;
  }
`;

const CartItemInfo = styled.div`
  flex: 1;
  min-width: 0;

  h4 {
    font-size: ${theme.typography.fontSize.base};
    font-weight: ${theme.typography.fontWeight.semiBold};
    margin-bottom: ${theme.spacing.xs};
    color: ${theme.colors.text};
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    ${media.down.sm} {
      font-size: 11px;
      margin-bottom: 1px;
    }
  }

  p {
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.textSecondary};

    ${media.down.sm} {
      font-size: 10px;
    }
  }
`;

const NotesInput = styled(Input)`
  margin-top: 4px;
  font-size: ${theme.typography.fontSize.sm};
  padding: 4px 8px;
  border: 1px solid ${theme.colors.border};
  min-height: 28px;
  
  &::placeholder {
    font-size: ${theme.typography.fontSize.xs};
    color: ${theme.colors.textLight};
  }

  ${media.down.sm} {
    font-size: 10px;
    padding: 3px 6px;
    min-height: 24px;
    margin-top: 2px;
  }
`;

const NoteToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 10px;
  color: ${theme.colors.primary};
  background: transparent;
  border: none;
  padding: 2px 0;
  cursor: pointer;
  margin-top: 2px;
  white-space: nowrap;
  
  svg {
    width: 10px;
    height: 10px;
  }
  
  &:hover {
    text-decoration: underline;
  }

  ${media.down.sm} {
    font-size: 9px;
    margin-top: 1px;
  }
`;

const OrderNotesSection = styled.div`
  margin-top: ${theme.spacing.lg};
  padding-top: ${theme.spacing.md};
  border-top: 1px solid ${theme.colors.border};

  ${media.down.sm} {
    margin-top: 8px;
    padding-top: 8px;
  }
`;

const OrderNotesLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semiBold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
  
  svg {
    width: 16px;
    height: 16px;
    color: ${theme.colors.primary};
  }

  ${media.down.sm} {
    font-size: 11px;
    margin-bottom: 4px;

    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const MobileCartToggle = styled.button`
  display: none;
  margin-left: auto;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${theme.colors.text};
  padding: ${theme.spacing.xs};
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.borderRadius.md};
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  min-width: 44px;
  min-height: 44px;

  &:hover {
    background-color: ${theme.colors.secondary};
  }

  &:active {
    transform: scale(0.9);
    background-color: ${theme.colors.secondary};
  }

  ${media.down.sm} {
    display: flex;
  }
`;

const FloatingCartButton = styled.button`
  display: none;
  position: fixed;
  bottom: ${theme.spacing.lg};
  right: ${theme.spacing.lg};
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryLight} 100%);
  color: ${theme.colors.white};
  border: none;
  cursor: pointer;
  box-shadow: ${theme.shadows.xl};
  align-items: center;
  justify-content: center;
  z-index: ${theme.zIndex.fixed + 1};
  transition: all 0.3s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:hover {
    transform: scale(1.1);
    box-shadow: ${theme.shadows['2xl']};
  }

  &:active {
    transform: scale(0.95);
  }

  ${media.down.sm} {
    display: ${({ show }) => show ? 'flex' : 'none'};
    width: 56px;
    height: 56px;
    bottom: 20px;
    right: 20px;
    z-index: 9999;
  }

  ${media.down.xs} {
    width: 52px;
    height: 52px;
    bottom: 16px;
    right: 16px;
  }

  .cart-badge {
    position: absolute;
    top: -5px;
    right: -5px;
    background: ${theme.colors.status.outOfStock};
    color: ${theme.colors.white};
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${theme.typography.fontSize.xs};
    font-weight: ${theme.typography.fontWeight.bold};
    min-width: 24px;
  }
`;

const CartItemActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  flex-shrink: 0;

  ${media.down.sm} {
    gap: 6px;
  }
`;

const RemoveButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: ${theme.borderRadius.md};
  background-color: ${theme.colors.status.outOfStock};
  color: ${theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    transform: scale(0.9);
  }

  ${media.down.sm} {
    width: 36px;
    height: 36px;
    min-width: 36px;
    min-height: 36px;
  }
`;

const CartTotal = styled.div`
  padding-top: ${theme.spacing.md};
  border-top: 2px solid ${theme.colors.border};
  margin-bottom: ${theme.spacing.lg};

  div {
    display: flex;
    justify-content: space-between;
    margin-bottom: ${theme.spacing.sm};
    font-size: ${theme.typography.fontSize.base};
    color: ${theme.colors.text};

    &:last-child {
      font-size: ${theme.typography.fontSize.xl};
      font-weight: ${theme.typography.fontWeight.bold};
      color: ${theme.colors.primary};
      margin-top: ${theme.spacing.md};
      padding-top: ${theme.spacing.md};
      border-top: 2px solid ${theme.colors.border};
    }
  }

  ${media.down.sm} {
    padding-top: 8px;
    margin-bottom: 12px;

    div {
      font-size: 12px;
      margin-bottom: 4px;

      &:last-child {
        font-size: 14px;
        margin-top: 8px;
        padding-top: 8px;
      }
    }
  }
`;

const ExistingOrderSection = styled(Card)`
  margin-bottom: ${theme.spacing.lg};
  padding: ${theme.spacing.lg};
  background: ${theme.colors.white};
  border: 2px solid ${theme.colors.primary};
  
  ${media.down.sm} {
    padding: 8px;
    margin-bottom: 8px;
    border-radius: 8px;
    border-width: 1px;
  }
`;

const ExistingOrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.md};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.border};
  flex-wrap: wrap;
  gap: 8px;
  
  h3 {
    font-size: ${theme.typography.fontSize.lg};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.text};
    display: flex;
    align-items: center;
    gap: ${theme.spacing.sm};

    ${media.down.sm} {
      font-size: 12px;
      gap: 4px;

      svg {
        width: 16px;
        height: 16px;
      }
    }
  }

  ${media.down.sm} {
    margin-bottom: 8px;
    padding-bottom: 8px;
    gap: 6px;
  }
`;

const ExistingOrderItems = styled.div`
  margin-bottom: ${theme.spacing.md};

  ${media.down.sm} {
    margin-bottom: 8px;
  }
`;

const ExistingOrderItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.sm} 0;
  border-bottom: 1px solid ${theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
  
  .item-info {
    flex: 1;
    min-width: 0;
    
    .item-name {
      font-weight: ${theme.typography.fontWeight.semiBold};
      color: ${theme.colors.text};
      margin-bottom: ${theme.spacing.xs};
      font-size: ${theme.typography.fontSize.base};

      ${media.down.sm} {
        font-size: 11px;
        margin-bottom: 2px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
    
    .item-details {
      font-size: ${theme.typography.fontSize.sm};
      color: ${theme.colors.textSecondary};

      ${media.down.sm} {
        font-size: 10px;
      }
    }
  }
  
  .item-actions {
    display: flex;
    align-items: center;
    gap: ${theme.spacing.sm};
    flex-shrink: 0;

    ${media.down.sm} {
      gap: 4px;

      button {
        width: 24px;
        height: 24px;
        min-width: 24px;
        min-height: 24px;
        padding: 0;

        svg {
          width: 12px;
          height: 12px;
        }
      }
    }
  }

  ${media.down.sm} {
    flex-direction: row;
    align-items: center;
    gap: 8px;
    padding: 6px 0;
  }
`;

const OrderStatusBadge = styled.span`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semiBold};
  background: ${({ status }) => {
    if (status === 'completed' || status === 'ready_for_billing') return `${theme.colors.status.ready}20`;
    if (status === 'preparing') return `${theme.colors.status.preparing}20`;
    return `${theme.colors.status.pending}20`;
  }};
  color: ${({ status }) => {
    if (status === 'completed' || status === 'ready_for_billing') return theme.colors.status.ready;
    if (status === 'preparing') return theme.colors.status.preparing;
    return theme.colors.status.pending;
  }};
  white-space: nowrap;

  ${media.down.sm} {
    font-size: 9px;
    padding: 2px 6px;
  }
`;

const EditOrderButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  ${media.down.sm} {
    min-height: 32px;
    padding: 4px 10px;
    font-size: 11px;
    gap: 4px;

    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const categories = ["Hot Drinks","Cold Drinks","Salads", "Rayata Special ","Breakfast","Sandwiches","South Indian","Chinese","Chinese Rice","Soup","Paneer Starters","Papad","Basmati Special","Marathi Special","Main Course/Veg Special","Main Course Paneer Special","Punjabi Dish","Tandoor Roti","Paratha"];

// Define tables as per requirements
const tables = [
  // Indian Baithak 1-7
  ...Array.from({ length: 7 }, (_, i) => ({
    id: `baithak${i+1}`,
    number: (i + 1).toString(),
    label: `Indian Baithak ${i+1}`,
    section: 'Indian Baithak',
    status: 'available',
  })),
  // Courtyard 1-8
  ...Array.from({ length: 8 }, (_, i) => ({
    id: `courtyard${i+1}`,
    number: (i + 1).toString(),
    label: `Courtyard ${i+1}`,
    section: 'Courtyard',
    status: 'available',
  })),
  // Dinning 1-20
  ...Array.from({ length: 20 }, (_, i) => ({
    id: `dinning${i+1}`,
    number: (i + 1).toString(),
    label: `Dinning ${i+1}`,
    section: 'Dinning',
    status: 'available',
  })),
  // Garden 1-10
  ...Array.from({ length: 10 }, (_, i) => ({
    id: `garden${i+1}`,
    number: (i + 1).toString(),
    label: `Garden ${i+1}`,
    section: 'Garden',
    status: 'available',
  })),
  // AC Hall 1-8
  ...Array.from({ length: 8 }, (_, i) => ({
    id: `achall${i+1}`,
    number: (i + 1).toString(),
    label: `AC Hall ${i+1}`,
    section: 'AC Hall',
    status: 'available',
  })),
];

// Mock existing orders data
const mockExistingOrders = {
  5: {
    id: 'ORD-001',
    tableNumber: 5,
    status: 'preparing',
    items: [
      { id: 1, name: 'Paneer Tikka', quantity: 2, price: 250 },
      { id: 2, name: 'Butter Naan', quantity: 4, price: 50 },
    ],
    total: 700,
    timestamp: '2:30 PM',
  },
  12: {
    id: 'ORD-002',
    tableNumber: 12,
    status: 'pending',
    items: [
      { id: 2, name: 'Chicken Biryani', quantity: 2, price: 350 },
    ],
    total: 700,
    timestamp: '2:45 PM',
  },
};

/* Menu items are provided by MenuContext (real-time shared source of truth) */

function WaiterOrdering() {
  // Get logged-in waiter from localStorage (set in Login.js after successful login)
  const waiterUser = JSON.parse(localStorage.getItem('waiterUser'));
  const waiterName = waiterUser?.name || waiterUser?.userId || 'Waiter';
  const navigate = useNavigate();
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [menuSearch, setMenuSearch] = useState('');
  const { menuItems, loading: menuLoading, error: menuError, fetchMenu } = useMenu();
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState({});
  const [existingOrders, setExistingOrders] = useState({});
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orderNotes, setOrderNotes] = useState('');

  // Fetch existing orders from backend
  useEffect(() => {
    fetchExistingOrders();
    // Poll for updates every 5 seconds
    const interval = setInterval(fetchExistingOrders, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchExistingOrders = async () => {
    try {
      const orders = await ordersService.getAll();
      // Group orders by table number
      // Exclude completed, cancelled, and ready_for_billing orders (sent to counter)
      // Tables with ready_for_billing orders should be available for new orders
      const ordersByTable = {};
      orders.forEach(order => {
        if (order.status !== 'completed' && 
            order.status !== 'cancelled' && 
            order.status !== 'ready_for_billing') {
          // Map items to include notes from specialInstructions
          const mappedItems = (order.items || []).map(item => ({
            ...item,
            notes: item.specialInstructions || item.notes || '',
            name: item.itemName || item.name,
          }));
          // Use section + tableNumber as unique key
          // If section is empty, default to 'Dinning'
          const section = order.section || 'Dinning';
          const tableKey = section + '-' + order.tableNumber;
          ordersByTable[tableKey] = {
            id: order.id,
            orderNumber: order.orderNumber,
            tableNumber: order.tableNumber,
            section: section,
            status: order.status,
            items: mappedItems,
            total: parseFloat(order.total) || 0,
            subtotal: parseFloat(order.subtotal) || 0,
            gst: parseFloat(order.gst) || 0,
            discount: parseFloat(order.discount) || 0,
            waiterName: order.waiterName || '',
            notes: order.notes || '',
            createdAt: order.createdAt
          };
        }
      });
      setExistingOrders(ordersByTable);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const handleLogout = () => {
    navigate('/login');
  };

  const handleAddToCart = (item) => {
    if (!item.available) return;
    const existingItem = cart.find((cartItem) => cartItem.id === item.id);
    if (existingItem) {
      setCart(
        cart.map((cartItem) =>
          cartItem.id === item.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        )
      );
    } else {
      setCart([...cart, { ...item, quantity: 1, notes: '' }]);
    }
  };

  const handleUpdateQuantity = (itemId, change) => {
    setCart(
      cart.map((item) => {
        if (item.id === itemId) {
          const newQuantity = item.quantity + change;
          if (newQuantity <= 0) return null;
          return { ...item, quantity: newQuantity };
        }
        return item;
      }).filter(Boolean)
    );
  };

  const handleRemoveFromCart = (itemId) => {
    setCart(cart.filter((item) => item.id !== itemId));
  };

  const handleUpdateItemNotes = (itemId, notes) => {
    setCart(
      cart.map((item) =>
        item.id === itemId ? { ...item, notes: notes } : item
      )
    );
  };

  const toggleNoteInput = (itemId) => {
    setExpandedNotes(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handlePlaceOrder = async () => {
    if (!selectedTable || cart.length === 0) return;
    
    setLoading(true);
    try {
      const orderItems = cart.map(item => ({
        name: item.name,
        quantity: item.quantity,
        price: item.price,
        notes: item.notes || '',
        specialInstructions: item.notes || '', // Backend expects specialInstructions
      }));

      const tableKey = (selectedTable.section ? selectedTable.section + '-' : '') + selectedTable.number;
      if (isEditingOrder && existingOrders[tableKey]) {
        // Update existing order
        const existingOrder = existingOrders[tableKey];
        
        await ordersService.update(existingOrder.id, {
          items: orderItems,
          subtotal: subtotal,
          gst: gst,
          acHallCharge: acHallCharge,
          discount: 0,
          total: total,
          notes: orderNotes.trim() || null,
          tableLabel: selectedTable.label,
          section: selectedTable.section,
        });

        alert(`Order updated for ${selectedTable.label}!`);
        await fetchExistingOrders();
      } else {
        // Create new order
        const newOrder = await ordersService.create({
          tableNumber: selectedTable.number.toString(),
          tableLabel: selectedTable.label,
          section: selectedTable.section,
          waiterName: waiterName, // Set from logged-in user
          items: orderItems,
          subtotal: subtotal,
          gst: gst,
          acHallCharge: acHallCharge,
          discount: 0,
          total: total,
          status: 'pending',
        });

        alert(`Order placed for ${selectedTable.label}! Order sent to kitchen.`);
        await fetchExistingOrders();
      }
      
      setCart([]);
      setOrderNotes('');
      setIsEditingOrder(false);
    } catch (error) {
      console.error('Error placing order:', error);
      alert(`Failed to place order: ${error.message || 'Please try again'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditOrder = () => {
    const tableKey = (selectedTable.section ? selectedTable.section + '-' : '') + selectedTable.number;
    if (!selectedTable || !existingOrders[tableKey]) return;
    setIsEditingOrder(true);
    const existingOrder = existingOrders[tableKey];
    // Load existing order items into cart
    const cartItems = (existingOrder.items || []).map(item => {
      const menuItem = menuItems.find(mi => mi.id === item.id || mi.name === item.name || mi.name === item.itemName);
      return {
        ...(menuItem || { id: Date.now(), name: item.itemName || item.name, price: item.price, category: 'All', available: true }),
        quantity: item.quantity || item.qty || 1,
        notes: item.notes || item.specialInstructions || '',
      };
    });
    setCart(cartItems);
    setOrderNotes(existingOrder.notes || '');
    setCartOpen(true);
  };

  const handleRemoveFromExistingOrder = async (tableNumber, itemId) => {
    const order = existingOrders[tableNumber];
    if (!order) return;
    
    try {
      const updatedItems = (order.items || []).filter(item => {
        return item.id !== itemId && item.itemName !== itemId && item.name !== itemId;
      });
      
      if (updatedItems.length === 0) {
        // Delete order if no items left
        await ordersService.remove(order.id);
        await fetchExistingOrders();
        setSelectedTable(null);
        return;
      }
      
      const newSubtotal = updatedItems.reduce((sum, item) => {
        const price = parseFloat(item.price || 0);
        const qty = item.quantity || item.qty || 1;
        return sum + (price * qty);
      }, 0);
      const newGst = newSubtotal * 0.05;
      const newTotal = newSubtotal + newGst;
      
      await ordersService.update(order.id, {
        items: updatedItems.map(item => ({
          name: item.itemName || item.name,
          quantity: item.quantity || item.qty,
          price: parseFloat(item.price),
        })),
        subtotal: newSubtotal,
        gst: newGst,
        total: newTotal,
      });
      
      await fetchExistingOrders();
    } catch (error) {
      console.error('Error removing item from order:', error);
      alert('Failed to remove item. Please try again.');
    }
  };

  const handleSendToCounter = async (selectedTable) => {
    const tableKey = (selectedTable.section ? selectedTable.section + '-' : '') + selectedTable.number;
    const order = existingOrders[tableKey];
    if (!order) {
      alert('No order found for this table');
      return;
    }

    if (order.status === 'ready_for_billing') {
      alert('Order already sent to counter for billing!');
      return;
    }

    if (!window.confirm(`Send order ${order.orderNumber || order.id} to counter for billing?`)) {
      return;
    }

    setLoading(true);
    try {
      await ordersService.update(order.id, {
        status: 'ready_for_billing',
      });

      alert(`Order sent to counter successfully! Table ${selectedTable.label} is ready for billing.`);
      await fetchExistingOrders();
    } catch (error) {
      console.error('Error sending order to counter:', error);
      alert(`Failed to send order to counter: ${error.message || 'Please try again'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateExistingOrderQuantity = async (tableNumber, itemId, change) => {
    const order = existingOrders[tableNumber];
    if (!order) return;
    
    try {
      const updatedItems = (order.items || []).map(item => {
        const itemMatch = item.id === itemId || item.itemName === itemId || item.name === itemId;
        if (itemMatch) {
          const newQuantity = (item.quantity || item.qty || 1) + change;
          if (newQuantity <= 0) return null;
          return { 
            ...item, 
            quantity: newQuantity,
            qty: newQuantity
          };
        }
        return item;
      }).filter(Boolean);
      
      if (updatedItems.length === 0) {
        // Delete order if no items left
        await ordersService.remove(order.id);
        await fetchExistingOrders();
        setSelectedTable(null);
        return;
      }
      
      const newSubtotal = updatedItems.reduce((sum, item) => {
        const price = parseFloat(item.price || 0);
        const qty = item.quantity || item.qty || 1;
        return sum + (price * qty);
      }, 0);
      const newGst = newSubtotal * 0.05;
      const newTotal = newSubtotal + newGst;
      
      await ordersService.update(order.id, {
        items: updatedItems.map(item => ({
          name: item.itemName || item.name,
          quantity: item.quantity || item.qty,
          price: parseFloat(item.price),
        })),
        subtotal: newSubtotal,
        gst: newGst,
        total: newTotal,
      });
      
      await fetchExistingOrders();
    } catch (error) {
      console.error('Error updating order quantity:', error);
      alert('Failed to update order. Please try again.');
    }
  };

  const baseMenu = selectedCategory === 'All'
    ? menuItems
    : menuItems.filter((item) => item.category === selectedCategory);

  const filteredMenu = menuSearch.trim()
    ? baseMenu.filter((item) => item.name.toLowerCase().includes(menuSearch.toLowerCase()))
    : baseMenu;

  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const gst = subtotal * 0.05;
  // Add 20% extra for AC Hall tables
  const isAcHall = selectedTable && selectedTable.section === 'AC Hall';
  const acHallCharge = isAcHall ? subtotal * 0.2 : 0;
  const total = subtotal + gst + acHallCharge;

  // Cart does NOT auto-open when adding items - user must tap floating button

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > parseInt(theme.breakpoints.sm)) {
        setCartOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset editing mode when table changes
  useEffect(() => {
    setIsEditingOrder(false);
    setCart([]);
  }, [selectedTable?.section, selectedTable?.number]);

  const currentOrder = selectedTable ? existingOrders[(selectedTable.section ? selectedTable.section + '-' : '') + selectedTable.number] : null;

  return (
    <Container>
      <Header>
        <h1>Waiter Ordering</h1>
        <HeaderActions>
          <TableInfo>Table: {selectedTable ? selectedTable.label : 'Select a table'}</TableInfo>
          <LogoutButton variant="secondary" size="sm" onClick={handleLogout}>
            <LogOut size={18} />
            <span className="logout-text">Logout</span>
          </LogoutButton>
        </HeaderActions>
      </Header>

      <TableGrid>
        {tables.map((table) => {
          const tableKey = (table.section ? table.section + '-' : '') + table.number;
          const order = existingOrders[tableKey];
          let status = table.status;
          let statusLabel = '';
          let labelColor = '#d97706';
          if (order) {
            if (order.status === 'pending') {
              status = 'pending';
              statusLabel = 'P';
              labelColor = '#d97706';
            } else if (order.status === 'preparing') {
              status = 'preparing';
              statusLabel = 'Pr';
              labelColor = '#2563eb';
            } else if (order.status === 'ready_for_billing') {
              status = 'ready_for_billing';
              statusLabel = 'R';
              labelColor = '#059669';
            }
          }
          return (
            <TableCard
              key={table.id}
              selected={selectedTable?.id === table.id}
              status={status}
              onClick={() => setSelectedTable(table)}
              style={{ minHeight: 50, padding: '8px', borderRadius: 10, position: 'relative' }}
            >
              <h3 style={{ fontSize: '1em', margin: 0 }}>{table.label}</h3>
              {statusLabel && (
                <span style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  background: '#fff',
                  color: labelColor,
                  border: `1px solid ${labelColor}`,
                  borderRadius: 8,
                  padding: '1px 4px',
                  fontSize: 10,
                  fontWeight: 600,
                  zIndex: 2
                }}>{statusLabel}</span>
              )}
            </TableCard>
          );
        })}
      </TableGrid>

      {currentOrder && (
        <ExistingOrderSection>
          <ExistingOrderHeader>
            <h3>
              <Package size={20} />
              Existing Order - {currentOrder.orderNumber || currentOrder.id}
            </h3>
            <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
              <OrderStatusBadge status={currentOrder.status}>
                {currentOrder.status === 'ready_for_billing' ? 'Ready for Billing' : 
                 currentOrder.status.charAt(0).toUpperCase() + currentOrder.status.slice(1)}
              </OrderStatusBadge>
              {currentOrder.status !== 'ready_for_billing' && currentOrder.status !== 'cancelled' && (
                <EditOrderButton
                  variant="primary"
                  size="sm"
                  onClick={handleEditOrder}
                >
                  <Edit size={16} />
                  Edit Order
                </EditOrderButton>
              )}
              {currentOrder.status !== 'ready_for_billing' && currentOrder.status !== 'cancelled' && (
                <EditOrderButton
                  variant="accent"
                  size="sm"
                  onClick={() => handleSendToCounter(selectedTable)}
                  disabled={loading}
                >
                  <Send size={16} />
                  Send to Counter
                </EditOrderButton>
              )}
              {currentOrder.status === 'ready_for_billing' && (
                <EditOrderButton
                  variant="secondary"
                  size="sm"
                  disabled
                >
                  <CheckCircle size={16} />
                  Sent to Counter
                </EditOrderButton>
              )}
            </div>
          </ExistingOrderHeader>
          
          <ExistingOrderItems>
            {(currentOrder.items || []).map((item, index) => {
              const itemName = item.itemName || item.name || 'Item';
              const quantity = item.quantity || item.qty || 1;
              const price = parseFloat(item.price || 0);
              const itemId = item.id || itemName || index;
              return (
                <ExistingOrderItem key={itemId || index}>
                  <div className="item-info">
                    <div className="item-name">{itemName}</div>
                    <div className="item-details">
                      ₹{price} × {quantity} = ₹{(price * quantity).toFixed(2)}
                    </div>
                    {(item.notes || item.specialInstructions) && (
                      <div style={{
                        marginTop: theme.spacing.xs,
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.colors.primary,
                        fontStyle: 'italic',
                        paddingLeft: theme.spacing.xs,
                        borderLeft: `2px solid ${theme.colors.primary}40`,
                      }}>
                        <FileText size={12} style={{ marginRight: theme.spacing.xs, verticalAlign: 'middle' }} />
                        {item.notes || item.specialInstructions}
                      </div>
                    )}
                  </div>
                  <div className="item-actions">
                    <QuantityControls>
                      <QuantityButton
                        onClick={() => handleUpdateExistingOrderQuantity(selectedTable.number, itemId, -1)}
                      >
                        <Minus size={14} />
                      </QuantityButton>
                      <QuantityValue>{quantity}</QuantityValue>
                      <QuantityButton
                        onClick={() => handleUpdateExistingOrderQuantity(selectedTable.number, itemId, 1)}
                      >
                        <Plus size={14} />
                      </QuantityButton>
                    </QuantityControls>
                    <RemoveButton
                      onClick={() => handleRemoveFromExistingOrder(selectedTable.number, itemId)}
                    >
                      <X size={14} />
                    </RemoveButton>
                  </div>
                </ExistingOrderItem>
              );
            })}
          </ExistingOrderItems>
          
          {currentOrder.notes && (
            <div style={{
              padding: theme.spacing.md,
              marginTop: theme.spacing.md,
              backgroundColor: `${theme.colors.primary}10`,
              borderRadius: theme.borderRadius.md,
              borderLeft: `3px solid ${theme.colors.primary}`,
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.xs,
                marginBottom: theme.spacing.xs,
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.semiBold,
                color: theme.colors.text,
              }}>
                <FileText size={14} />
                Order Notes:
              </div>
              <div style={{
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.textSecondary,
                lineHeight: 1.5,
              }}>
                {currentOrder.notes}
              </div>
            </div>
          )}
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            paddingTop: theme.spacing.md,
            borderTop: `2px solid ${theme.colors.border}`,
            marginTop: theme.spacing.md,
            flexWrap: 'wrap',
            gap: theme.spacing.sm
          }}>
            <div>
              <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
                {currentOrder.createdAt ? `Time: ${new Date(currentOrder.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : currentOrder.timestamp ? `Time: ${currentOrder.timestamp}` : ''}
              </div>
            </div>
            <div style={{ 
              fontSize: theme.typography.fontSize.lg, 
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.colors.primary
            }}>
              Total: ₹{currentOrder.total}
            </div>
          </div>
        </ExistingOrderSection>
      )}

      <ContentGrid>
        <MenuSection>
          <div style={{ marginBottom: theme.spacing.md }}>
            <Input
              placeholder="Search menu items..."
              value={menuSearch}
              onChange={(e) => setMenuSearch(e.target.value)}
            />
          </div>

          {menuLoading && (
            <div style={{ padding: theme.spacing.lg, textAlign: 'center', color: theme.colors.textSecondary }}>
              Loading menu items...
            </div>
          )}

          {menuError && (
            <div style={{ 
              padding: theme.spacing.lg, 
              marginBottom: theme.spacing.lg,
              backgroundColor: '#fee2e2',
              borderRadius: theme.borderRadius.md,
              color: '#dc2626',
              border: '1px solid #fecaca'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: theme.spacing.xs }}>Error Loading Menu</div>
              <div style={{ fontSize: theme.typography.fontSize.sm, marginBottom: theme.spacing.md }}>
                {menuError}
              </div>
              <Button 
                variant="primary" 
                size="sm"
                onClick={fetchMenu}
                style={{ marginTop: theme.spacing.sm }}
              >
                Retry
              </Button>
            </div>
          )}

          {!menuLoading && !menuError && menuItems.length === 0 && (
            <div style={{ padding: theme.spacing.lg, textAlign: 'center', color: theme.colors.textSecondary }}>
              No menu items available
            </div>
          )}

          {menuSearch.trim() && (
            <div style={{ marginBottom: theme.spacing.md }}>
              <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary, marginBottom: theme.spacing.sm }}>Related</div>
              <MenuGrid>
                {menuItems
                  .filter((item) => item.name.toLowerCase().includes(menuSearch.toLowerCase()))
                  .slice(0, 8)
                  .map((item) => (
                    <MenuItemCard
                      key={`rel-${item.id}`}
                      disabled={!item.available}
                      onClick={() => handleAddToCart(item)}
                    >
                      <AvailabilityBadge available={item.available}>
                        {item.available ? 'Available' : 'Out of Stock'}
                      </AvailabilityBadge>
                      <MenuItemName>{item.name}</MenuItemName>
                      <MenuItemPrice>₹{item.price}</MenuItemPrice>
                      <QuantitySelector>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleAddToCart(item); }}
                          disabled={!item.available}
                        >
                          <Plus size={16} />
                        </Button>
                      </QuantitySelector>
                    </MenuItemCard>
                  ))}
              </MenuGrid>
            </div>
          )}

          {!menuLoading && !menuError && (
            <>
              <CategoryTabs>
                {categories.map((category) => (
                  <CategoryTab
                    key={category}
                    active={selectedCategory === category}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category}
                  </CategoryTab>
                ))}
              </CategoryTabs>

              <MenuGrid>
                {filteredMenu.map((item) => (
                  <MenuItemCard
                    key={item.id}
                    disabled={!item.available}
                    onClick={() => handleAddToCart(item)}
                  >
                    <AvailabilityBadge available={item.available}>
                      {item.available ? 'Available' : 'Out of Stock'}
                    </AvailabilityBadge>
                    <MenuItemName>{item.name}</MenuItemName>
                    <MenuItemPrice>₹{item.price}</MenuItemPrice>
                    <QuantitySelector>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddToCart(item);
                        }}
                        disabled={!item.available}
                      >
                        <Plus size={16} />
                      </Button>
                    </QuantitySelector>
                  </MenuItemCard>
                ))}
              </MenuGrid>
            </>
          )}
        </MenuSection>

        <CartBackdrop className={cartOpen ? 'open' : ''} onClick={() => setCartOpen(false)} />

        <CartSection className={cartOpen ? 'open' : ''} onClick={(e) => e.stopPropagation()}>
          <CartHeader>
            <ShoppingCart size={24} />
            <h2>Order Cart {cart.length > 0 && <span>({cart.length})</span>}</h2>
            <MobileCartToggle
              onClick={() => setCartOpen(!cartOpen)}
              className="mobile-only"
            >
              {cartOpen ? '×' : '☰'}
            </MobileCartToggle>
          </CartHeader>

          <CartItems>
            {cart.length === 0 ? (
              <p style={{ textAlign: 'center', color: theme.colors.textSecondary, padding: theme.spacing.xl }}>
                Cart is empty
              </p>
            ) : (
              cart.map((item) => (
                <CartItem key={item.id}>
                  <CartItemRow>
                    <CartItemInfo>
                      <h4>{item.name}</h4>
                      <p>₹{item.price} × {item.quantity} = ₹{item.price * item.quantity}</p>
                      {item.notes && (
                        <p style={{ fontSize: '10px', color: theme.colors.primary, fontStyle: 'italic' }}>
                          Note: {item.notes}
                        </p>
                      )}
                      {(expandedNotes[item.id] || item.notes) ? (
                        <NotesInput
                          type="text"
                          placeholder="Add note..."
                          value={item.notes || ''}
                          onChange={(e) => handleUpdateItemNotes(item.id, e.target.value)}
                          autoFocus={expandedNotes[item.id]}
                        />
                      ) : (
                        <NoteToggle onClick={() => toggleNoteInput(item.id)}>
                          <Plus size={12} />
                          Add note
                        </NoteToggle>
                      )}
                    </CartItemInfo>
                    <CartItemActions>
                      <QuantityControls>
                        <QuantityButton
                          onClick={() => handleUpdateQuantity(item.id, -1)}
                          style={{ width: '24px', height: '24px' }}
                        >
                          <Minus size={12} />
                        </QuantityButton>
                        <QuantityValue>{item.quantity}</QuantityValue>
                        <QuantityButton
                          onClick={() => handleUpdateQuantity(item.id, 1)}
                          style={{ width: '24px', height: '24px' }}
                        >
                          <Plus size={12} />
                        </QuantityButton>
                      </QuantityControls>
                      <RemoveButton
                        onClick={() => handleRemoveFromCart(item.id)}
                        style={{ width: '24px', height: '24px' }}
                      >
                        <X size={12} />
                      </RemoveButton>
                    </CartItemActions>
                  </CartItemRow>
                </CartItem>
              ))
            )}
          </CartItems>

          {cart.length > 0 && (
            <>
              <CartTotal>
                <div>
                  <span>Subtotal:</span>
                  <span>₹{subtotal.toFixed(2)}</span>
                </div>
                <div>
                  <span>GST (5%):</span>
                  <span>₹{gst.toFixed(2)}</span>
                </div>
                <div>
                  <span>Total:</span>
                  <span>₹{total.toFixed(2)}</span>
                </div>
              </CartTotal>

              <OrderNotesSection>
                <OrderNotesLabel>
                  <FileText size={16} />
                  Order Notes (Optional)
                </OrderNotesLabel>
                <NotesInput
                  type="text"
                  placeholder="Add general notes (e.g., Customer birthday)"
                  value={orderNotes}
                  onChange={(e) => setOrderNotes(e.target.value)}
                  rows={2}
                  as="textarea"
                  style={{
                    minHeight: '50px',
                    resize: 'vertical',
                    fontFamily: theme.typography.fontFamily,
                  }}
                />
              </OrderNotesSection>

              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={handlePlaceOrder}
                disabled={!selectedTable || loading}
                style={{
                  marginTop: theme.spacing.md,
                  minHeight: '44px',
                  fontSize: '14px',
                }}
              >
                {loading ? 'Placing...' : isEditingOrder ? 'Update' : 'Place Order'}
              </Button>
            </>
          )}
        </CartSection>
      </ContentGrid>

      {cart.length > 0 && !cartOpen && (
        <FloatingCartButton 
          show={cart.length > 0 && !cartOpen}
          onClick={() => setCartOpen(true)}
        >
          <ShoppingCart size={24} />
          {cart.length > 0 && (
            <span className="cart-badge">{cart.length}</span>
          )}
        </FloatingCartButton>
      )}

      <Footer hasSidebar={false} />
    </Container>
  );
}

export default WaiterOrdering;

